import { useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { hasPermission, Permission, type CommunityStructure } from "@atrium/shared";
import { Button } from "../../components/ui/Button";
import { toast } from "../../components/ui/Toast";
import { IconStaffShield } from "../../components/socialIcons";
import { IconEyeOff, IconSuspend, IconWarning } from "../../components/icons";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { fullDate } from "../../lib/format";

/*
 * How a community's standing with Atrium shows up inside it:
 *   - suspended: members see only a notice; staff see the community with a banner
 *   - limited: managers see why it's hidden from discovery
 *   - warnings: managers see each one until they acknowledge it
 */

const until = (iso: string | null) => (iso ? ` until ${fullDate(iso)}` : "");

/** Full-pane notice for members of a suspended community (staff never see it). */
export function SuspendedNotice({ structure }: { structure: CommunityStructure }) {
  const c = structure.community;
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="mb-3 flex size-11 items-center justify-center rounded-xl bg-danger-soft text-danger">
        <IconSuspend className="icon size-[22px]" aria-hidden />
      </div>
      <h2 className="text-lg font-semibold text-fg">{c.name} has been suspended</h2>
      <p className="mt-1 max-w-md text-sm text-fg-2">
        Atrium staff suspended this community{until(c.platform.until)} for breaking the community guidelines. Its channels, messages and voice are unavailable while it's suspended.
      </p>
      {c.platform.reason && (c.isOwner || hasPermission(c.myPermissions, Permission.MANAGE_COMMUNITY)) && (
        <p className="mt-4 max-w-md whitespace-pre-wrap rounded-lg bg-surface px-3.5 py-3 text-left text-sm text-fg-2">{c.platform.reason}</p>
      )}
      <Link to="/c" className="mt-5">
        <Button>Back to communities</Button>
      </Link>
    </div>
  );
}

function Bar({ tone, icon: I, children, action }: { tone: "danger" | "warning" | "staff"; icon: typeof IconWarning; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div role="status" className={cn("flex shrink-0 items-start gap-2.5 border-b border-line px-4 py-2.5 text-sm", tone === "danger" ? "bg-danger-soft" : "bg-surface")}>
      <I className={cn("icon mt-0.5 size-[18px] shrink-0", tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : "text-staff")} aria-hidden />
      <div className="min-w-0 flex-1 text-fg-2">{children}</div>
      {action}
    </div>
  );
}

export function PlatformBanner({ structure }: { structure: CommunityStructure }) {
  const c = structure.community;
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const manages = c.isOwner || hasPermission(c.myPermissions, Permission.MANAGE_COMMUNITY);
  const warning = c.platform.warnings?.[0];

  const ack = async (id: string) => {
    setBusy(id);
    try {
      await api.post(`/api/communities/${c.id}/platform-warnings/${id}/ack`);
      await qc.invalidateQueries({ queryKey: ["structure", c.id] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {c.viewerIsStaff && c.platform.status === "suspended" && (
        <Bar tone="danger" icon={IconStaffShield}>
          <span className="font-medium text-fg">Suspended{until(c.platform.until)}.</span> Only Atrium staff can see inside. {c.platform.reason}
        </Bar>
      )}
      {c.platform.status === "limited" && (manages || c.viewerIsStaff) && (
        <Bar tone="warning" icon={IconEyeOff}>
          <span className="font-medium text-fg">Limited by Atrium staff{until(c.platform.until)}.</span> {c.name} is hidden from discovery and search. {c.platform.reason}
        </Bar>
      )}
      {warning && manages && !c.viewerIsStaff && (
        <Bar
          tone="warning"
          icon={IconWarning}
          action={
            <Button size="sm" variant="ghost" loading={busy === warning.id} onClick={() => void ack(warning.id)}>
              Acknowledge
            </Button>
          }
        >
          <span className="font-medium text-fg">A warning from Atrium staff.</span> <span className="whitespace-pre-wrap">{warning.reason}</span>
          {(c.platform.warnings?.length ?? 0) > 1 && <span className="text-fg-3"> · {c.platform.warnings!.length - 1} more</span>}
        </Bar>
      )}
    </>
  );
}
